// cspell:ignore Boxify
import type { RedoAdminRpcClient } from "@redotech/admin-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import DownloadIcon from "@redotech/redo-icons/download-01.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  FileUploadState,
  RedoFileUpload,
} from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { downloadCsv, toCsv } from "../../../utils/csv";

/** Mirrors the Boxify export so a downloaded template imports with no mapping. */
const EXAMPLE_CSV_HEADERS = [
  "ProductTitle",
  "VariantTitle",
  "VariantSKU",
  "Length(in)",
  "Width(in)",
  "Height(in)",
  "Padding(in)",
  "VariantId",
];

const EXAMPLE_CSV_ROWS = [
  [
    "Classic Tee",
    "Small / Blue",
    "TEE-S-BLU",
    "12",
    "10",
    "2",
    "0.5",
    "[34188251523]",
  ],
  [
    "Classic Tee",
    "Medium / Blue",
    "TEE-M-BLU",
    "12",
    "10",
    "2",
    "0.5",
    "[34188251524]",
  ],
];

const MAPPABLE_FIELDS = [
  { key: "variantId", label: "Shopify variant ID", required: true },
  { key: "length", label: "Length", required: true },
  { key: "width", label: "Width", required: true },
  { key: "height", label: "Height", required: true },
  { key: "padding", label: "Padding", required: true },
  { key: "sku", label: "SKU (optional)", required: false },
  { key: "productTitle", label: "Product title (optional)", required: false },
  { key: "variantTitle", label: "Variant title (optional)", required: false },
] as const;

type MappableField = (typeof MAPPABLE_FIELDS)[number]["key"];
type ColumnMapping = Partial<Record<MappableField, string>>;
type KeyBy = "variantId" | "sku";

const KEY_BY_OPTIONS: { value: KeyBy; label: string }[] = [
  { value: "sku", label: "SKU" },
  { value: "variantId", label: "Shopify variant ID" },
];

type ImportResult = Awaited<
  ReturnType<RedoAdminRpcClient["importVariantDimensionsCsv"]>
>;

function listItem<T extends string>(value: T, label: string): RedoListItem<T> {
  return { id: value, type: "text", text: label, value };
}

function generateExampleCsv(): string {
  return toCsv([EXAMPLE_CSV_HEADERS, ...EXAMPLE_CSV_ROWS]);
}

export const ImportVariantDimensionsCsvCard = memo(
  function ImportVariantDimensionsCsvCard({
    teamId,
  }: {
    teamId: string | undefined;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [force, setForce] = useState(false);
    const [mapping, setMapping] = useState<ColumnMapping>({});
    const [keyBy, setKeyBy] = useState<KeyBy>("sku");
    const [result, setResult] = useState<ImportResult | null>(null);

    // Only a dry run that got far enough to count variants can be applied, so
    // the Apply button stays disabled until validation has actually passed.
    // Held as the result itself rather than a boolean so the button can read
    // the variant count without a redundant null check.
    const validatedResult =
      result &&
      !result.applied &&
      result.missingColumns.length === 0 &&
      result.variantsToUpdate > 0
        ? result
        : null;

    const keyByOptions: RedoListItem<KeyBy>[] = useMemo(
      () =>
        KEY_BY_OPTIONS.map((option) => listItem(option.value, option.label)),
      [],
    );

    const headerOptions: RedoListItem<string>[] = useMemo(
      () => (result?.headers ?? []).map((header) => listItem(header, header)),
      [result?.headers],
    );

    const resetForNewFile = (nextFile: File | null) => {
      setFile(nextFile);
      setResult(null);
      setMapping({});
      setForce(false);
      setKeyBy("sku");
    };

    const handleFilesAdded = (files: FileList | null) => {
      const selected = files?.[0];
      if (selected) {
        resetForNewFile(selected);
      }
    };

    // The dropzone renders its own file chip, so it needs the selection
    // mirrored as upload state with loading already finished.
    const uploadState: FileUploadState<File>[] = file
      ? [
          {
            name: file.name,
            value: file,
            pendingBytes: false,
            bytesTotal: file.size,
          },
        ]
      : [];

    const handleDownloadExample = useHandler(() => {
      downloadCsv(generateExampleCsv(), "variant-dimensions-example.csv");
    });

    const runImport = useHandler(async (dryRun: boolean) => {
      if (!teamId || !rpcClient || !file) {
        return;
      }

      try {
        setLoading(true);
        const response = await rpcClient.importVariantDimensionsCsv(
          { teamId, dryRun, keyBy, mapping, force },
          { files: [file] },
        );
        setResult(response);

        // On the first look at a file, adopt the key the sheet can support so
        // the operator is not left guessing why nothing resolved.
        if (!result) {
          setKeyBy(response.suggestedKeyBy);
        }

        // Adopt the server's resolved mapping so the dropdowns show what
        // auto-detection picked and the operator only corrects what's wrong,
        // but keep any column the operator explicitly cleared: the server
        // omits those, and dropping the marker would let the next validate
        // auto-detect them all over again.
        setMapping((current) => {
          const next: ColumnMapping = { ...response.resolvedMapping };
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          for (const field of Object.keys(current) as MappableField[]) {
            if (current[field] === "") {
              next[field] = "";
            }
          }
          return next;
        });

        if (response.errors.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          toast(response.errors[0]!, { variant: "error" });
        } else if (response.applied) {
          toast(
            `Wrote ${response.metafieldsWritten} metafields across ${response.variantsToUpdate} variants`,
            { variant: "success" },
          );
        } else {
          toast(
            `Validated ${response.variantsToUpdate} variants — nothing written yet`,
            { variant: "success" },
          );
        }
      } catch (error) {
        toast("Failed to import variant dimensions CSV", { variant: "error" });
        console.error("Failed to import variant dimensions CSV:", error);
      } finally {
        setLoading(false);
      }
    });

    return (
      <Card title="Product Dimensions CSV Import">
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" textColor="secondary">
            Sets length, width, height, and padding variant metafields in
            Shopify from a dimensions spreadsheet. Validate first — nothing is
            written until you click Apply.
          </Text>

          <Flex align="center" gap="md">
            <RedoButton
              hierarchy="secondary"
              IconLeading={DownloadIcon}
              onClick={handleDownloadExample}
              size="sm"
              text="Download Example CSV"
              theme="normal"
            />
          </Flex>

          <RedoFileUpload
            accept=".csv"
            acceptMessage="CSV file"
            disabled={loading}
            files={uploadState}
            fullWidth
            handleFileDeleted={() => resetForNewFile(null)}
            handleFilesAdded={handleFilesAdded}
            hideDropzoneAfterSelection
          />

          {result && result.headers.length > 0 && (
            <Flex dir="column" gap="sm">
              <RedoSingleSelectDropdownInput
                description="SKU is looked up in this store's catalog. Use Shopify variant ID only if your sheet contains Shopify's numeric IDs."
                fullWidth
                label="Match variants by"
                options={keyByOptions}
                optionSelected={({ item }) => {
                  if (item && typeof item !== "string" && item.value) {
                    setKeyBy(item.value);
                  }
                }}
                selectedItem={keyByOptions.find(
                  (option) => option.value === keyBy,
                )}
                size="sm"
              />

              <Text fontSize="sm" fontWeight="medium">
                Column mapping
              </Text>
              <Text fontSize="xs" textColor="secondary">
                Detected from your file. Change any that guessed wrong — pick a
                column again to clear it — then validate again.
              </Text>
              {MAPPABLE_FIELDS.map((field) => {
                const selectedColumn = mapping[field.key];
                const isMissing = result.missingColumns.includes(field.key);
                return (
                  <RedoSingleSelectDropdownInput
                    fullWidth
                    key={field.key}
                    label={field.label}
                    options={headerOptions}
                    optionSelected={({ item, selected }) => {
                      if (!item || typeof item === "string" || !item.value) {
                        return;
                      }
                      // Re-picking the current option deselects it; honour that
                      // so a column set by mistake can be cleared rather than
                      // only swapped for another. Empty string, not undefined:
                      // undefined is dropped in transit and the server would
                      // fall back to auto-detecting the field again.
                      setMapping((current) => ({
                        ...current,
                        [field.key]: selected ? item.value : "",
                      }));
                    }}
                    selectedItem={headerOptions.find(
                      (option) => option.value === selectedColumn,
                    )}
                    size="sm"
                    state={isMissing ? "error" : "default"}
                  />
                );
              })}
            </Flex>
          )}

          {result && (
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                {result.applied ? "Import result" : "Validation result"}
              </Text>
              {result.shopName && (
                <Text fontSize="sm" textColor="secondary">
                  Store: {result.shopName} ({result.myshopifyDomain})
                </Text>
              )}
              <Text fontSize="sm" textColor="secondary">
                Rows read: {result.rowsParsed} · Variants to update:{" "}
                {result.variantsToUpdate} · Skipped (missing dimensions):{" "}
                {result.skippedMissingDimensions} · Skipped (no key):{" "}
                {result.skippedMissingKey}
                {result.skippedInvalidVariantId > 0
                  ? ` · Skipped (non-numeric variant ID): ${result.skippedInvalidVariantId}`
                  : ""}
              </Text>
              {!result.includesPadding && result.variantsToUpdate > 0 && (
                <Text fontSize="sm" textColor="secondary">
                  No padding column — writing length, width, and height only.
                </Text>
              )}
              {result.unmatchedSkus.length > 0 && (
                <Text fontSize="sm" textColor="warning">
                  {result.unmatchedSkus.length} SKU(s) matched no variant in
                  this store: {result.unmatchedSkus.slice(0, 5).join(", ")}
                  {result.unmatchedSkus.length > 5 ? ", ..." : ""}
                </Text>
              )}
              {result.ambiguousSkus.length > 0 && (
                <Text fontSize="sm" textColor="warning">
                  {result.ambiguousSkus.length} SKU(s) matched more than one
                  variant and were skipped:{" "}
                  {result.ambiguousSkus.slice(0, 5).join(", ")}
                  {result.ambiguousSkus.length > 5 ? ", ..." : ""}
                </Text>
              )}
              {result.overwriteCount > 0 && (
                <Text fontSize="sm" textColor="secondary">
                  Existing values that will be overwritten:{" "}
                  {result.overwriteCount}
                </Text>
              )}
              {result.hasBlockingConflicts && (
                <Text fontSize="sm" textColor="error">
                  Conflicting metafield types detected — Shopify will reject
                  these writes unless you check Force.
                </Text>
              )}
              {result.applied && (
                <Text fontSize="sm" textColor="secondary">
                  Metafields written: {result.metafieldsWritten} · failed:{" "}
                  {result.metafieldsFailed}
                </Text>
              )}
              {result.previewVariants.length > 0 && (
                <Flex dir="column" gap="xs" pt="sm">
                  <Text fontSize="xs" fontWeight="medium">
                    First {result.previewVariants.length} of{" "}
                    {result.variantsToUpdate}:
                  </Text>
                  {result.previewVariants.map((variant) => (
                    <Text
                      fontSize="xs"
                      key={variant.variantGid}
                      textColor="secondary"
                    >
                      {variant.sku || variant.variantGid} — {variant.length} ×{" "}
                      {variant.width} × {variant.height}
                      {variant.padding ? `, padding ${variant.padding}` : ""}
                    </Text>
                  ))}
                </Flex>
              )}
              {result.warnings.slice(0, 5).map((warning) => (
                <Text fontSize="xs" key={warning} textColor="secondary">
                  {warning}
                </Text>
              ))}
              {result.errors.map((error) => (
                <Text fontSize="xs" key={error} textColor="error">
                  {error}
                </Text>
              ))}
            </Flex>
          )}

          {result?.hasBlockingConflicts && (
            <Flex align="center" gap="sm">
              <RedoCheckbox
                setValue={(checked) => setForce(checked === true)}
                size="sm"
                value={force}
              />
              <Text fontSize="sm" fontWeight="medium">
                Force — write despite conflicting metafield types
              </Text>
            </Flex>
          )}

          <Flex align="center" gap="md">
            <RedoButton
              disabled={!file || loading}
              hierarchy="secondary"
              onClick={() => runImport(true)}
              pending={loading}
              size="md"
              text="Validate"
              theme="normal"
            />
            <RedoButton
              disabled={!validatedResult || loading}
              hierarchy="primary"
              onClick={() => runImport(false)}
              pending={loading}
              size="md"
              text={
                validatedResult
                  ? `Apply to ${validatedResult.variantsToUpdate} variants`
                  : "Apply"
              }
              theme="normal"
            />
          </Flex>
        </Flex>
      </Card>
    );
  },
);
